'use client'

import { useSignals } from '@preact/signals-react/runtime'
import { baseURL } from '@susu/headless-commerce/config/config'
import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import { isNotNull } from '@susu/null'
import Icon from '@susu/size-selector/components/Icon/Icon'
import classNames from 'classnames'
import { useI18N } from 'packages/i18n/dist/client'
import {
  type ChangeEventHandler,
  type MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { useClientNav } from '../ClientNavProvider'
import styles from './NavSearchInput.module.scss'

export type ClientNavSearchInputProps = {
  className?: string
}

export const ClientNavSearchInput = ({
  className,
}: ClientNavSearchInputProps) => {
  useSignals()
  const { t } = useI18N()
  const locale = useLocale()
  const {
    clearSearchQuery,
    searchQuery,
    setSearchQuery,
    isNavOpened,
    isSubMenuOpened,
    isSearchOpenFromHeader,
  } = useClientNav()

  const searchInputRef = useRef<HTMLInputElement | null>(null)
  const searchInputClass = classNames(className)

  const handleSearchCriteriaChanged: ChangeEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        setSearchQuery(event.target.value)
      },
      [setSearchQuery],
    )

  const handleCloseButtonClicked: MouseEventHandler<HTMLButtonElement> =
    useCallback(() => {
      clearSearchQuery()
      searchInputRef.current?.focus()
    }, [clearSearchQuery])

  const handleSubmit = useCallback(() => {
    const newURL = new URL(baseURL())

    newURL.searchParams.set('q', searchQuery.value)
    newURL.pathname = `/${locale}/search`

    window.location.href = newURL.toString()
  }, [locale, searchQuery.value])

  const handleKeyUp = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      handleSubmit()
    },
    [handleSubmit],
  )

  const isEmpty = searchQuery.value === ''

  // Focus the input when the nav is opened.
  useEffect(() => {
    setTimeout(() => {
      if (
        isNavOpened.value === true &&
        isSubMenuOpened.value['SearchMenu'] === true &&
        isNotNull(searchInputRef.current)
      ) {
        searchInputRef.current.focus()
      }
    }, 500)
  }, [isNavOpened.value, isSubMenuOpened.value])

  useEffect(() => {
    if (!isSearchOpenFromHeader.value) {
      clearSearchQuery()
    }
  }, [isSearchOpenFromHeader.value, clearSearchQuery])

  return (
    <>
      <input
        className={searchInputClass}
        onChange={handleSearchCriteriaChanged}
        onKeyUp={handleKeyUp}
        onSubmit={handleSubmit}
        placeholder={t('navigation.search_placeholder')}
        ref={searchInputRef}
        type="search"
        value={searchQuery.value}
        data-testid="nav-search-input"
      />
      {!isEmpty && (
        <button
          type="button"
          className={styles['nav__search-clear-btn']}
          onClick={handleCloseButtonClicked}
          data-testid="button_search_clear"
        >
          <Icon icon="close" aria-label="Clear search" />
          <span className="sr-only">Clear</span>
        </button>
      )}
    </>
  )
}
